import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { showActionsModal, showModal } from './Modal.js';
import { showCarDetailsModal } from './CarDetailsModal.js';
import { zl } from './carDetailsRows.mjs';
import { showToast } from './Toast.js';

// The card you get by walking up to a car in the showroom: the spec sheet, the price, and
// the two things you can do about it here — buy this model, or sell the one you already
// own back to the dealer. Repairs live with the mechanic (showMechanicMenu below), because
// a broken car is not standing in the showroom, it is sitting in your garage.
//
//   await showCarCardModal(scene, 'panda');
//   await showMechanicMenu(scene);

/**
 * Why the „Kup” button is grey, in the order the server checks them
 * (backend cars.py buy_car: licence → slot → money). null = you can buy it.
 */
export function buyBlockReason({ cars, driving, money }, price) {
  if (!driving?.has_licence) return 'Potrzebujesz prawa jazdy {licence}';
  if ((cars?.free_slots ?? 0) <= 0) return 'Brak miejsca w garażu {yard}';
  if ((money ?? 0) < price) return 'Za mało pieniążków {coin}';
  return null;
}

// The one car of this model you could sell right now: yours, at home in the garage, not
// broken (`can_sell` is derived server-side and says exactly that). A broken one is
// reported separately so the button can explain itself instead of vanishing.
function ownedOfModel(model) {
  const owned = (gameState.raw?.cars?.owned ?? []).filter((c) => c.model === model);
  return { sellable: owned.find((c) => c.can_sell), broken: owned.find((c) => c.broken) };
}

export async function showCarCardModal(scene, model) {
  const state = gameState.raw ?? {};
  const spec = state.catalog?.cars?.[model];
  if (!spec) return;

  const reason = buyBlockReason(state, spec.price);
  const actions = [{
    label: reason ? `Kup za ${zl(spec.price)} — ${reason}` : `Kup za ${zl(spec.price)}`,
    disabled: !!reason,
    fn: async () => {
      try {
        await gameState.carsBuy(model);
        showToast(scene, 'Kupione! Auto czeka w garażu {party}');
      } catch (e) {
        showToast(scene, messageFor(e));
      }
      return null;
    },
  }];

  const { sellable, broken } = ownedOfModel(model);
  const ratio = state.catalog?.fuel?.sell_ratio ?? 0.6;
  if (sellable) {
    const payout = Math.round(spec.price * ratio);
    actions.push({
      label: `Sprzedaj ${sellable.plate} za ${zl(payout)}`,
      fn: async () => {
        const ok = await showModal(scene, {
          title: 'Na pewno?',
          body: `${spec.name_pl} · ${sellable.plate}\nSalon odkupi je za ${zl(payout)}.`,
          confirmLabel: 'Sprzedaj',
        });
        if (!ok) return null;
        try {
          await gameState.carsSell(sellable.id);
          showToast(scene, `Sprzedane za ${zl(payout)} {coin}`);
        } catch (e) {
          showToast(scene, messageFor(e));
        }
        return null;
      },
    });
  } else if (broken) {
    actions.push({ label: 'Zepsute — napraw w serwisie {wrench}', disabled: true });
  }

  await showCarDetailsModal(scene, spec, actions);
}

/**
 * Pan Mechanik: one row per broken car of yours. Repairs are the only thing he does —
 * fuel is the station's job and selling is the salesman's.
 */
export async function showMechanicMenu(scene) {
  const state = gameState.raw ?? {};
  const cost = state.catalog?.fuel?.misfuel_repair ?? 1500;
  const broken = (state.cars?.owned ?? []).filter((c) => c.broken);

  if (!broken.length) {
    await showActionsModal(scene, {
      title: 'Pan Mechanik',
      body: 'Wszystkie Twoje auta są sprawne {thumbs_up}',
      actions: [],
    });
    return;
  }

  await showActionsModal(scene, {
    title: 'Pan Mechanik',
    body: `Naprawa po złym paliwie: ${zl(cost)}`,
    actions: broken.map((car) => ({
      label: `Napraw ${car.plate} — ${zl(cost)}`,
      disabled: (state.money ?? 0) < cost,
      fn: async () => {
        try {
          await gameState.carsRepair(car.id);
          showToast(scene, 'Naprawione! {wrench}');
        } catch (e) {
          showToast(scene, messageFor(e));
        }
        return null;
      },
    })),
  });
}
